import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useLaboratorios } from '../hooks/useVisualizaciones'
import type { VideoSeguimientoCreateInput } from '../types'

const schema = z.object({
  base_wp: z.string().min(1, 'Base WP es requerida'),
  id_video: z.string().min(1, 'ID de video es requerido'),
  nombre_video: z.string().optional(),
  laboratorio_id: z.coerce.number().int().positive('Selecciona un laboratorio'),
})

type FormData = z.infer<typeof schema>

interface Props {
  onSubmit: (data: VideoSeguimientoCreateInput) => void
  isSubmitting?: boolean
}

export function VisualizacionForm({ onSubmit, isSubmitting = false }: Props) {
  const { data: laboratorios, isLoading: cargandoLabs } = useLaboratorios()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { base_wp: '', id_video: '', nombre_video: '' },
  })

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data as VideoSeguimientoCreateInput))}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label htmlFor="base_wp">Base WP *</Label>
        <Input id="base_wp" {...register('base_wp')} placeholder="ej: produccion" disabled={isSubmitting} />
        {errors.base_wp && <p className="text-sm text-destructive">{errors.base_wp.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="id_video">ID Video *</Label>
        <Input id="id_video" {...register('id_video')} placeholder="ej: 12345" disabled={isSubmitting} />
        {errors.id_video && <p className="text-sm text-destructive">{errors.id_video.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="nombre_video">Nombre del video</Label>
        <Input
          id="nombre_video"
          {...register('nombre_video')}
          placeholder="Opcional"
          disabled={isSubmitting}
        />
      </div>

      <div className="space-y-2">
        <Label>Laboratorio *</Label>
        <Controller
          control={control}
          name="laboratorio_id"
          render={({ field }) => (
            <Select
              value={field.value ? String(field.value) : ''}
              onValueChange={(v) => field.onChange(Number(v))}
              disabled={isSubmitting || cargandoLabs}
            >
              <SelectTrigger>
                <SelectValue placeholder={cargandoLabs ? 'Cargando...' : 'Selecciona un laboratorio'} />
              </SelectTrigger>
              <SelectContent>
                {laboratorios?.map((lab) => (
                  <SelectItem key={lab.grupo_id} value={String(lab.grupo_id)}>
                    {lab.grupo_nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.laboratorio_id && (
          <p className="text-sm text-destructive">{errors.laboratorio_id.message}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-blue-800 hover:bg-blue-900 text-white"
      >
        {isSubmitting ? 'Guardando...' : 'Crear Seguimiento'}
      </Button>
    </form>
  )
}
